import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowDownToLine, ArrowUpFromLine, Search, Eye, EyeOff, CreditCard, Plus, Trash2, ExternalLink } from 'lucide-react'
import { PanelShell } from '../components/Layout'
import { Card, Button, Modal, Rows, Empty, Field, NumberInput, statusBadge, CopyButton } from '../components/UI'
import { CoinIcon } from '../components/Market'
import { Donut } from './Dashboard'
import { AddCardModal } from './Cards'
import { useAuth, toast } from '../store'
import { get, post, del } from '../lib/api'
import { num, fa, cardNumber, toEnDigits, asArray, clsx } from '../lib/format'

type Asset = {
  currency: string; faName: string; color: string; balance: number; locked: number
  total: number; address: string; networks: string[]; priceIrt: number; valueIrt: number
}

export default function WalletPage() {
  const { user, refresh } = useAuth()
  const nav = useNavigate()
  const [data, setData] = useState<{ total: number; totalUsdt: number; assets: Asset[] } | null>(null)
  const [portfolio, setPortfolio] = useState<any>(null)
  const [q, setQ] = useState('')
  const [hideSmall, setHideSmall] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [cards, setCards] = useState<any[]>([])

  const [depositOpen, setDepositOpen] = useState<Asset | null>(null)
  const [withdrawOpen, setWithdrawOpen] = useState<Asset | null>(null)
  const [cardOpen, setCardOpen] = useState(false)

  const load = () => {
    get('/wallet/balances').then(setData).catch(() => {})
    get('/wallet/portfolio').then(setPortfolio).catch(() => {})
    get('/wallet/cards').then((r: any) => setCards(asArray(r))).catch(() => {})
    refresh()
  }
  useEffect(load, [])

  // ── نتیجه بازگشت از درگاه پرداخت.
  //
  // درگاه، کاربر را با ?deposit=ok|fail|pending برمی‌گرداند. حالت pending
  // یعنی تأیید نزد درگاه هنوز قطعی نشده (مثلاً قطعی لحظه‌ای شبکه) — چند
  // ثانیه بعد دوباره از سرور پرسیده می‌شود تا نتیجه قطعی شود؛ نه پیام
  // موفقیتِ زودهنگام می‌دهیم نه شکستِ اشتباه.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search)
    const dep = sp.get('deposit')
    const order = sp.get('order') || ''
    if (!dep) return
    window.history.replaceState({}, '', '/wallet')
    if (dep === 'ok') {
      toast.ok('واریز با موفقیت انجام شد و به کیف پول شما اضافه شد')
      load()
      return
    }
    if (dep === 'fail') {
      toast.err('پرداخت ناموفق بود؛ اگر مبلغی کسر شده تا ۷۲ ساعت برمی‌گردد')
      return
    }
    if (dep === 'pending' && order) {
      let tries = 0
      const poll = () => {
        get(`/payments/paystar/status/${order}`).then((r: any) => {
          if (r.status === 'verified') { toast.ok('واریز تأیید و به کیف پول اضافه شد'); load() }
          else if (r.status === 'failed') toast.err(r.error || 'پرداخت ناموفق بود')
          else if (++tries < 6) setTimeout(poll, 3000)
          else toast.info('نتیجه پرداخت هنوز قطعی نیست؛ چند دقیقه دیگر تاریخچه را ببینید')
        }).catch(() => {})
      }
      poll()
    }
  }, [])

  // پاسخ سرور همیشه همان چیزی نیست که انتظار داریم (۵۰۰، پراکسی، تغییر قالب).
  // خواندن مستقیمِ portfolio.items تنها جای برنامه بود که این را فرض می‌کرد و
  // با یک پاسخ ناقص، کل صفحه را با «این بخش بالا نیامد» زمین می‌زد.
  const pItems = asArray<any>(portfolio, 'items')

  const assets = asArray<Asset>(data, 'assets').filter(
    (a) => (!q || (a.currency ?? '').toLowerCase().includes(q.toLowerCase()) || (a.faName ?? '').includes(q)) &&
           (!hideSmall || a.total > 0),
  )

  return (
    <PanelShell title="کیف پول" subtitle="مدیریت دارایی‌ها، واریز و برداشت">
      <div className="mb-5 grid gap-5 lg:grid-cols-3">
        <Card className="relative overflow-hidden lg:col-span-2">
          <div className="pointer-events-none absolute -left-16 -top-20 h-56 w-56 rounded-full bg-up/15 blur-[80px]" />
          <div className="relative">
            <div className="mb-1 flex items-center gap-2 text-sm text-slate-400">
              ارزش کل دارایی‌ها
              <button onClick={() => setHidden((h) => !h)} className="text-slate-500 transition hover:text-slate-300">
                {hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="num break-anywhere text-2xl font-black text-white xs:text-3xl sm:text-4xl">
              {hidden ? '••••••' : num(data?.total)} <span className="text-lg font-semibold text-slate-400">تومان</span>
            </div>
            <div className="num mt-1 text-xs text-slate-500">
              {hidden ? '••••' : `≈ ${num(data?.totalUsdt, 2)} تتر`}
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button onClick={() => setDepositOpen(assets.find((a) => a.currency === 'IRT') ?? null)}>
                <ArrowDownToLine className="h-4 w-4" /> واریز ریالی
              </Button>
              <Button variant="ghost" onClick={() => setWithdrawOpen(assets.find((a) => a.currency === 'IRT') ?? null)}>
                <ArrowUpFromLine className="h-4 w-4" /> برداشت ریالی
              </Button>
              <Link to="/crypto" className="btn-aqua text-sm">
                <ExternalLink className="h-4 w-4" /> واریز و برداشت رمزارز
              </Link>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="mb-4 font-bold text-white">ترکیب دارایی</h3>
          {!portfolio ? <Rows n={3} /> : pItems.length === 0
            ? <Empty text="هنوز دارایی ندارید" /> : <Donut items={pItems} />}
        </Card>
      </div>

      <Card className="mb-5 !p-0">
        <div className="flex flex-wrap items-center gap-3 border-b border-white/[.06] p-4">
          <h3 className="font-bold text-white">دارایی‌های من</h3>
          <label className="chip cursor-pointer text-[11px]">
            <input type="checkbox" className="h-3 w-3 accent-brand-500" checked={hideSmall}
              onChange={(e) => setHideSmall(e.target.checked)} />
            مخفی‌کردن موجودی صفر
          </label>
          <div className="relative mr-auto min-w-[180px] flex-1 sm:flex-none">
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input className="input !py-2 pr-9 text-xs" placeholder="جستجوی ارز" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>

        {/* ── موبایل: هر دارایی یک ردیف کارتی، بدون کشیدن افقی صفحه */}
        <div className="divide-y divide-white/[.05] md:hidden">
          {!data ? <div className="p-4"><Rows n={5} /></div> : assets.map((a) => (
            <div key={a.currency} className="flex items-center gap-3 px-3 py-3">
              <CoinIcon symbol={a.currency === 'IRT' ? 'TMN' : a.currency} color={a.color} size={36} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-white">{a.faName}</div>
                <div className="num text-[11px] text-slate-500" dir="ltr">{a.currency}</div>
              </div>
              <div className="shrink-0 text-left">
                <div className="num text-sm text-slate-100">{hidden ? '••••' : num(a.balance, 6)}</div>
                <div className="num text-[11px] text-slate-500">
                  {hidden ? '••••' : `${num(a.valueIrt)} تومان`}
                </div>
                {a.locked > 0 && !hidden && (
                  <div className="num text-[10px] text-gold">در سفارش: {num(a.locked, 6)}</div>
                )}
              </div>
              <div className="flex shrink-0 flex-col gap-1">
                <button onClick={() => (a.currency === 'IRT' ? setDepositOpen(a) : nav('/crypto'))}
                  className="rounded-lg border border-white/10 px-2.5 py-1.5 text-[11px] text-up active:bg-up/10">واریز</button>
                <button onClick={() => (a.currency === 'IRT' ? setWithdrawOpen(a) : nav('/crypto'))}
                  className="rounded-lg border border-white/10 px-2.5 py-1.5 text-[11px] text-down active:bg-down/10">برداشت</button>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden overflow-x-auto md:block">
          {!data ? <div className="p-4"><Rows n={6} /></div> : (
            <table className="w-full min-w-[620px] text-sm">
              <thead>
                <tr className="text-xs text-slate-500">
                  <th className="px-4 py-3 text-right font-medium">ارز</th>
                  <th className="px-4 py-3 text-right font-medium">موجودی</th>
                  <th className="px-4 py-3 text-right font-medium">در سفارش</th>
                  <th className="px-4 py-3 text-right font-medium">ارزش (تومان)</th>
                  <th className="px-4 py-3 text-left font-medium">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => (
                  <tr key={a.currency} className="border-t border-white/[.05] transition hover:bg-white/[.03]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <CoinIcon symbol={a.currency === 'IRT' ? 'TMN' : a.currency} color={a.color} size={32} />
                        <div>
                          <div className="font-semibold text-white">{a.faName}</div>
                          <div className="num text-[11px] text-slate-500" dir="ltr">{a.currency}</div>
                        </div>
                      </div>
                    </td>
                    <td className="num px-4 py-3 text-slate-200">{hidden ? '••••' : num(a.balance, 6)}</td>
                    <td className="num px-4 py-3 text-slate-500">{hidden ? '••••' : num(a.locked, 6)}</td>
                    <td className="num px-4 py-3 text-slate-300">{hidden ? '••••' : num(a.valueIrt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => (a.currency === 'IRT' ? setDepositOpen(a) : nav('/crypto'))}
                          className="rounded-lg border border-white/10 px-2.5 py-1.5 text-[11px] text-up transition hover:border-up/40 hover:bg-up/10">واریز</button>
                        <button onClick={() => (a.currency === 'IRT' ? setWithdrawOpen(a) : nav('/crypto'))}
                          className="rounded-lg border border-white/10 px-2.5 py-1.5 text-[11px] text-down transition hover:border-down/40 hover:bg-down/10">برداشت</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* کارت‌های بانکی */}
      <Card className="!p-0">
        <div className="flex items-center justify-between border-b border-white/[.06] p-4">
          <h3 className="font-bold text-white">کارت‌های بانکی</h3>
          <div className="flex items-center gap-2">
            {/* مدیریت کامل (وضعیت استعلام، دلیل رد، حذف) در بخش اختصاصی است */}
            <Link to="/cards" className="text-xs text-brand-300 hover:underline">مدیریت کارت‌ها</Link>
            <Button variant="ghost" className="!py-2 text-xs" onClick={() => setCardOpen(true)}>
              <Plus className="h-4 w-4" /> افزودن کارت
            </Button>
          </div>
        </div>
        <div className="grid gap-3 p-3 xs:grid-cols-1 sm:grid-cols-2 sm:p-4 lg:grid-cols-3">
          {cards.length === 0 ? <div className="sm:col-span-2 lg:col-span-3"><Empty text="کارت بانکی ثبت نشده است" icon={<CreditCard className="h-6 w-6" />} /></div>
            : cards.map((c) => (
              <div key={c.id} className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-brand-600/25 via-white/[.03] to-aqua/15 p-4">
                <div className="mb-6 flex items-center justify-between">
                  <span className="text-xs text-slate-300">{c.bankName}</span>
                  {statusBadge(c.status)}
                </div>
                {/* شماره پوشانده نمایش داده می‌شود؛ کارت بانکی روی صفحه‌ای که
                    ممکن است جلوی دیگری باز باشد، کامل لازم نیست */}
                <div className="num mb-2 text-lg tracking-widest text-white" dir="ltr">
                  {cardNumber(c.masked || c.cardNumber)}
                </div>
                <div className="flex items-center justify-between">
                  <span className="num text-[11px] text-slate-400" dir="ltr">{c.iban || '—'}</span>
                  <button
                    onClick={async () => {
                      try { await del(`/wallet/cards/${c.id}`); toast.ok('کارت حذف شد'); load() }
                      catch (e: any) { toast.err(e.message) }
                    }}
                    className="text-slate-500 transition hover:text-down"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
        </div>
      </Card>

      <DepositModal asset={depositOpen} onClose={() => setDepositOpen(null)} onDone={load} />
      <WithdrawModal asset={withdrawOpen} cards={cards} onClose={() => setWithdrawOpen(null)} onDone={load} />
      <AddCardModal open={cardOpen} onClose={() => setCardOpen(false)} onDone={load} />
    </PanelShell>
  )
}

/* ---------------------------------------------------------------- واریز */
function DepositModal({ asset, onClose, onDone }: { asset: Asset | null; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState('')
  const [network, setNetwork] = useState('')
  const [info, setInfo] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [cfg, setCfg] = useState<{ paystar: boolean; cashin: boolean; sandbox: boolean }>({ paystar: false, cashin: false, sandbox: false })
  const [method, setMethod] = useState<'gateway' | 'shenase'>('gateway')
  const [ci, setCi] = useState<any>(null)
  const isIrt = asset?.currency === 'IRT'

  useEffect(() => {
    setAmount(''); setInfo(null); setCi(null); setSbxOrder('')
    if (asset && isIrt) {
      get('/payments/config').then((r: any) => {
        setCfg(r)
        // اگر درگاه کارتی خاموش ولی شناسه‌دار روشن است، مستقیم روی شناسه‌دار
        setMethod(r.paystar ? 'gateway' : (r.cashin ? 'shenase' : 'gateway'))
      }).catch(() => {})
    }
    if (asset && !isIrt) {
      setNetwork(asset.networks?.[0] ?? '')
      get(`/wallet/deposit-address/${asset.currency}`).then(setInfo).catch(() => {})
    }
  }, [asset])

  useEffect(() => {
    if (isIrt && method === 'shenase' && cfg.cashin && !ci) {
      get('/payments/cashin/info').then(setCi).catch((e: any) => toast.err(e.message))
    }
  }, [method, cfg, isIrt])

  const [sbxOrder, setSbxOrder] = useState('')

  const submit = async () => {
    if (!asset) return
    setLoading(true)
    try {
      const r = await post<any>('/payments/paystar/create', { amount: Number(amount) })
      if (r.sandbox) {
        // حالت آزمایشی: به درگاه نمی‌رویم؛ صفحه‌ی تأییدِ آزمایشی نشان می‌دهیم
        setSbxOrder(r.orderId)
        setLoading(false)
        return
      }
      // درگاه واقعی: هدایت با فرمِ POST (با GET باز نمی‌شود)
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = r.paymentUrl
      const inp = document.createElement('input')
      inp.type = 'hidden'; inp.name = 'token'; inp.value = r.token
      form.appendChild(inp)
      document.body.appendChild(form)
      form.submit()
    } catch (e: any) { toast.err(e.message); setLoading(false) }
  }

  const sandboxPay = async (success: boolean) => {
    setLoading(true)
    try {
      await post('/payments/paystar/sandbox', { order_id: sbxOrder, success })
      toast[success ? 'ok' : 'err'](success ? 'واریز آزمایشی با موفقیت انجام شد' : 'پرداخت آزمایشی لغو شد')
      setSbxOrder(''); onDone(); onClose()
    } catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  return (
    <Modal open={!!asset} onClose={onClose} title={`واریز ${asset?.faName ?? ''}`}>
      {isIrt ? (
        sbxOrder ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-gold/30 bg-gold/[.08] p-4 text-xs leading-7 text-gold">
              <b>حالت آزمایشی</b> — این یک پرداخت واقعی نیست. برای شبیه‌سازیِ نتیجه یکی را انتخاب کنید.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="up" loading={loading} onClick={() => sandboxPay(true)} className="!py-3">پرداخت موفق</Button>
              <Button variant="down" loading={loading} onClick={() => sandboxPay(false)} className="!py-3">پرداخت ناموفق</Button>
            </div>
          </div>
        ) : (
        <>
          {cfg.sandbox && (
            <div className="mb-3 rounded-xl border border-gold/25 bg-gold/[.06] p-2.5 text-center text-[11px] text-gold">
              حالت آزمایشی روشن است — واریز و برداشت شبیه‌سازی می‌شوند و پول واقعی جابه‌جا نمی‌شود.
            </div>
          )}

          {/* دو بخشِ واریز ریالی: درگاه پرداخت و واریز شناسه‌دار */}
          <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
            {([['gateway', 'درگاه پرداخت'], ['shenase', 'واریز شناسه‌دار']] as const).map(([k, l]) => (
              <button key={k} onClick={() => setMethod(k)}
                className={clsx('rounded-lg py-2 text-xs font-bold transition',
                  method === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white' : 'text-slate-400')}>
                {l}
              </button>
            ))}
          </div>

          {method === 'gateway' ? (
            cfg.paystar ? (
              <>
                <Field label="مبلغ واریز (تومان)">
                  <NumberInput value={amount} onChange={setAmount} suffix="تومان" />
                </Field>
                <div className="mb-4 flex flex-wrap gap-2">
                  {[1_000_000, 5_000_000, 20_000_000, 100_000_000].map((v) => (
                    <button key={v} onClick={() => setAmount(String(v))} className="chip text-[11px] hover:border-brand-500/40 hover:text-brand-200">
                      {num(v)}
                    </button>
                  ))}
                </div>
                <div className="mb-4 rounded-xl border border-white/[.06] bg-white/[.02] p-3 text-[11px] leading-6 text-slate-400">
                  به درگاه امن پرداخت (شبکه شتاب) منتقل می‌شوید و با هر کارت بانکی می‌توانید
                  پرداخت کنید. پس از پرداخت، مبلغ به‌صورت خودکار به کیف پول شما اضافه می‌شود.
                </div>
                <Button loading={loading} onClick={submit} className="w-full !py-3" disabled={!Number(amount)}>
                  {cfg.sandbox ? 'پرداخت آزمایشی' : 'انتقال به درگاه پرداخت'}
                </Button>
              </>
            ) : (
              <div className="rounded-xl border border-white/[.08] bg-white/[.02] p-4 text-xs leading-7 text-slate-400">
                درگاه پرداخت هنوز فعال نشده است. مدیر سایت باید از تنظیمات، درگاه پی‌استار را
                فعال کند.
              </div>
            )
          ) : (
            cfg.cashin ? (
              <ShenaseDeposit ci={ci} sandbox={cfg.sandbox} onDone={onDone} onClose={onClose} />
            ) : (
              <div className="rounded-xl border border-white/[.08] bg-white/[.02] p-4 text-xs leading-7 text-slate-400">
                واریز شناسه‌دار هنوز فعال نشده است. مدیر سایت باید از تنظیمات، واریز شناسه‌دار
                را فعال کند.
              </div>
            )
          )}
        </>
        )
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border border-brand-500/25 bg-brand-500/[.07] p-4 text-xs leading-7 text-brand-200">
            واریز {asset?.faName} به صورت زنجیره‌ای انجام می‌شود: در صفحه «واریز و برداشت رمزارز»
            شبکه را انتخاب کنید و آدرس اختصاصی خود را دریافت نمایید.
          </div>
          <Link to="/crypto" className="btn-primary w-full !py-3" onClick={onClose}>
            رفتن به واریز رمزارز
          </Link>
        </div>
      )}
    </Modal>
  )
}

/* ------------------------------------------------- واریز شناسه‌دار */
function ShenaseDeposit({ ci, sandbox, onDone, onClose }: { ci: any; sandbox?: boolean; onDone?: () => void; onClose?: () => void }) {
  const [amt, setAmt] = useState('')
  const [busy, setBusy] = useState(false)
  if (!ci) return <Rows n={3} />
  const acc = ci.account || {}
  const rows: [string, string][] = [
    ['شناسه واریز شما', ci.identifier || '—'],
    ['شماره شبا مقصد', acc.iban || '—'],
    ['به نام', acc.name || '—'],
    ['بانک', acc.bank || '—'],
  ]
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-brand-500/25 bg-brand-500/[.07] p-3 text-[11px] leading-6 text-brand-100">
        با کارت‌به‌کارت این روش کار نمی‌کند. از «انتقال حساب به حساب» (پایا/ساتنا) یا
        اپلیکیشن بانک، به شبای زیر واریز کنید و <b>حتماً شناسه واریز خود را در فیلد
        «شناسه واریز» وارد کنید</b>. مبلغ پس از تأیید بانک، خودکار به کیف پول شما اضافه می‌شود.
      </div>
      <div className="divide-y divide-white/[.06] rounded-xl border border-white/[.07] bg-white/[.02]">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-3 px-3 py-2.5">
            <span className="text-[11px] text-slate-500">{k}</span>
            <span className="flex items-center gap-2">
              <span className={clsx('num text-slate-100', k.includes('شناسه') && 'font-extrabold text-brand-200')} dir="ltr">{v}</span>
              {v && v !== '—' && <CopyButton text={v.replace(/\s/g, '')} />}
            </span>
          </div>
        ))}
      </div>
      {acc.minIrt > 0 && (
        <p className="text-[11px] text-slate-500">حداقل مبلغ هر واریز: {num(acc.minIrt)} تومان</p>
      )}
      <p className="text-[11px] leading-6 text-amber-300/80">
        شناسه واریز مخصوص حساب شماست؛ اگر آن را وارد نکنید یا اشتباه بزنید، واریز به‌طور
        خودکار شناسایی نمی‌شود و بررسی دستی زمان می‌برد.
      </p>
      {sandbox && (
        <div className="rounded-xl border border-gold/25 bg-gold/[.06] p-3">
          <div className="mb-2 text-[11px] text-gold">شبیه‌سازیِ واریز (حالت آزمایشی)</div>
          <div className="flex gap-2">
            <div className="flex-1"><NumberInput value={amt} onChange={setAmt} suffix="تومان" /></div>
            <Button loading={busy} disabled={!Number(amt)} onClick={async () => {
              setBusy(true)
              try {
                await post('/payments/cashin/sandbox', { amount: Number(amt) })
                toast.ok('واریز شناسه‌دار آزمایشی انجام شد'); onDone?.(); onClose?.()
              } catch (e: any) { toast.err(e.message) } finally { setBusy(false) }
            }}>ثبت واریز آزمایشی</Button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- برداشت */
function WithdrawModal({ asset, cards, onClose, onDone }: { asset: Asset | null; cards: any[]; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState('')
  const [address, setAddress] = useState('')
  const [network, setNetwork] = useState('')
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const isIrt = asset?.currency === 'IRT'

  useEffect(() => {
    setAmount(''); setAddress(''); setOtp('')
    if (asset) setNetwork(asset.networks?.[0] ?? '')
    if (asset?.currency === 'IRT' && cards[0]) setAddress(cards[0].cardNumber)
  }, [asset])

  const submit = async () => {
    if (!asset) return
    setLoading(true)
    try {
      await post('/wallet/withdraw', {
        currency: asset.currency, amount: Number(amount), address, network, otp,
      })
      toast.ok('درخواست برداشت ثبت شد و در حال بررسی است')
      onDone(); onClose()
    } catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  return (
    <Modal open={!!asset} onClose={onClose} title={`برداشت ${asset?.faName ?? ''}`}>
      <div className="mb-4 flex items-center justify-between rounded-xl border border-white/[.06] bg-white/[.02] px-3 py-2.5 text-xs">
        <span className="text-slate-500">موجودی قابل برداشت</span>
        <span className="num text-white">{num(asset?.balance, 6)} {asset?.currency}</span>
      </div>

      {isIrt ? (
        <Field label="واریز به کارت">
          {cards.length === 0 ? (
            <div className="rounded-xl border border-down/20 bg-down/[.06] p-3 text-xs text-down">
              ابتدا یک کارت بانکی ثبت کنید.
            </div>
          ) : (
            <select className="input" value={address} onChange={(e) => setAddress(e.target.value)}>
              {cards.map((c) => (
                <option key={c.id} value={c.cardNumber} className="bg-ink-900">
                  {c.bankName} — {c.cardNumber}
                </option>
              ))}
            </select>
          )}
        </Field>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border border-brand-500/25 bg-brand-500/[.07] p-4 text-xs leading-7 text-brand-200">
            برداشت {asset?.faName} از صفحه «واریز و برداشت رمزارز» انجام می‌شود؛ آنجا می‌توانید شبکه،
            آدرس مقصد و کارمزد دقیق شبکه را ببینید.
          </div>
          <Link to="/crypto" className="btn-primary w-full !py-3" onClick={onClose}>
            رفتن به برداشت رمزارز
          </Link>
        </div>
      )}

      {isIrt && (
        <>
          <Field label="مبلغ برداشت">
            <NumberInput value={amount} onChange={setAmount} suffix={asset?.currency} />
          </Field>
          <Field label="کد دومرحله‌ای (در صورت فعال بودن)">
            <input className="input num tracking-[.4em]" dir="ltr" maxLength={6} value={otp}
              onChange={(e) => setOtp(toEnDigits(e.target.value).replace(/\D/g, ''))} />
          </Field>

          <div className="mb-4 rounded-xl border border-white/[.06] bg-white/[.02] p-3 text-[11px] leading-6 text-slate-400">
            کارمزد برداشت ریالی {fa('۲۵٬۰۰۰')} تومان است و تسویه در چرخه‌های پایا انجام می‌شود.
          </div>

          <Button variant="down" loading={loading} onClick={submit} className="w-full !py-3" disabled={!Number(amount)}>
            ثبت درخواست برداشت
          </Button>
        </>
      )}
    </Modal>
  )
}
